import { ReactElement } from 'react';

/**
 * Calculates how many child elements can be displayed within a container
 * before overflowing, based on fixed element widths and container width.
 *
 * @param containerWidth - Total available width of the container (in pixels).
 * @param childWidth - Width of a single child element (in pixels).
 * @param content - The full list of items to be laid out.
 * @param allowExtraElement - When `true`, allows one additional
 * item to be shown if there is enough leftover space for it.
 * @param reservedEndWidth - Width to reserve at the end of the row (e.g. for a fixed end element).
 *
 * @returns visibleCount - Number of items that can be displayed without overflowing.
 * @returns visibleItems - The portion of `content` that fits in the container.
 * @returns overflowItems - Remaining items that exceed available space.
 * @example
 * // With containerWidth: 320 and childWidth: 100:
 * // 320 / 100 = 3.2 → 3 items could fit, but the function reserves
 * // one slot for the "more" element, leaving only 2 visible items.
 *
 * const { visibleItems, overflowItems } = getLayoutOverflowConfig(
 *   320, 100, content, false
 * );
 *
 * // visibleItems: ['A', 'B']
 * // overflowItems: ['C', 'D']
 * @remarks
 * - The calculation assumes all child elements have identical width.
 */
export const getLayoutOverflowConfig = (
    containerWidth: number,
    childWidth: number,
    content: ReactElement[],
    allowExtraElement: boolean,
    reservedEndWidth = 0
) => {
    const availableWidth = containerWidth - reservedEndWidth;
    const maxVisible = Math.max(Math.floor(availableWidth / childWidth) - 1, 0);

    const allowOneMore = allowExtraElement && availableWidth - (maxVisible + 1) * childWidth >= 0;

    const visibleCount = allowOneMore ? maxVisible + 1 : maxVisible;

    const visibleItems = content.slice(0, visibleCount);
    const overflowItems = content.slice(visibleCount);

    return { visibleCount, visibleItems, overflowItems };
};
